import { useCallback, useEffect, useState } from "react";
import adminApi from "../api/adminApi";
import { createPhotosessionStyle } from "../utils/defaults";
import {
  moveArrayItem,
  prependArrayItem,
  removeArrayItem,
  updateArrayItem
} from "../utils/arrayEditors";

function getDefaultPhotosessionConfig() {
  return {
    pageSize: 5,
    tokenCost: 8000,
    oneTimeProductId: 9,
    styles: []
  };
}

export function useAdminPhotosession({ adminToken, enabled }) {
  const [photosessionConfig, setPhotosessionConfig] = useState(null);
  const [photosessionLoading, setPhotosessionLoading] = useState(false);
  const [photosessionSaving, setPhotosessionSaving] = useState(false);
  const [photosessionDirty, setPhotosessionDirty] = useState(false);
  const [photosessionUploadingIndex, setPhotosessionUploadingIndex] = useState(null);

  const load = useCallback(async (token = adminToken) => {
    if (!token) return;
    setPhotosessionLoading(true);
    try {
      const res = await adminApi.getPhotosessionConfig(token);
      setPhotosessionConfig(res.data || null);
      setPhotosessionDirty(false);
    } catch (error) {
      console.error("Ошибка загрузки фотосессии:", error);
    } finally {
      setPhotosessionLoading(false);
    }
  }, [adminToken]);

  const save = useCallback(async () => {
    if (!adminToken || !photosessionConfig) return;
    setPhotosessionSaving(true);
    try {
      const res = await adminApi.savePhotosessionConfig(adminToken, photosessionConfig);
      if (res.data?.config) {
        setPhotosessionConfig(res.data.config);
      }
      setPhotosessionDirty(false);
    } catch (error) {
      console.error("Ошибка сохранения фотосессии:", error);
      window.alert("Ошибка сохранения фотосессии");
    } finally {
      setPhotosessionSaving(false);
    }
  }, [adminToken, photosessionConfig]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    load(adminToken);
  }, [enabled, adminToken, load]);

  const updateConfig = useCallback((patch) => {
    setPhotosessionConfig((prev) => (prev ? { ...prev, ...patch } : prev));
    setPhotosessionDirty(true);
  }, []);

  const mutateStyles = useCallback((updater) => {
    setPhotosessionConfig((prev) => {
      const base = prev || getDefaultPhotosessionConfig();
      const styles = Array.isArray(base.styles) ? base.styles : [];
      return {
        ...base,
        styles: updater(styles)
      };
    });
    setPhotosessionDirty(true);
  }, []);

  const uploadPreview = useCallback(async (styleIndex, file) => {
    if (!adminToken || !file) return;
    setPhotosessionUploadingIndex(styleIndex);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminApi.uploadPhotosessionPreview(adminToken, formData);
      const attachment = String(res.data?.attachment || "").trim();
      if (!attachment) {
        throw new Error("empty attachment");
      }
      mutateStyles((styles) => updateArrayItem(styles, styleIndex, { previewAttachment: attachment }));
    } catch (error) {
      console.error("Ошибка загрузки превью фотосессии:", error);
      window.alert("Ошибка загрузки картинки");
    } finally {
      setPhotosessionUploadingIndex(null);
    }
  }, [adminToken, mutateStyles]);

  const styles = Array.isArray(photosessionConfig?.styles) ? photosessionConfig.styles : [];

  return {
    state: {
      photosessionConfig,
      photosessionLoading,
      photosessionSaving,
      photosessionDirty,
      photosessionUploadingIndex,
      styles
    },
    actions: {
      reload: () => load(adminToken),
      save,
      updateConfig,
      updateStyle: (index, patch) => mutateStyles((list) => updateArrayItem(list, index, patch)),
      moveStyle: (index, direction) => mutateStyles((list) => moveArrayItem(list, index, direction)),
      removeStyle: (index) => mutateStyles((list) => removeArrayItem(list, index)),
      addStyle: () => mutateStyles((list) => prependArrayItem(list, createPhotosessionStyle())),
      uploadPreview
    }
  };
}

export default useAdminPhotosession;
